import React from "react";

/**
 * ValueIcon — renders a single gradient medallion for a Core Value or Anti-Value.
 *
 * Icons live in /app/frontend/public/icons/values/<id>.svg. We load them as
 * <img src> rather than inlining them because every SVG in the pack declares
 * the same `linearGradient id="g"` — inlining multiple at once would
 * collide. Using <img> gives each SVG its own document scope for free.
 *
 * The `anti` flag is informational only today (all icons in the pack are
 * already colour-coded violet for values / coral for anti-values), but it
 * lets callers opt into extra treatment like a red ring later without API
 * churn.
 */
const FALLBACK_LETTERS_BG = "bg-gradient-to-br from-violet-400 to-indigo-500";
const FALLBACK_LETTERS_BG_ANTI = "bg-gradient-to-br from-rose-400 to-amber-500";

/** Convert a display name into the catalog `id` slug used by the SVG pack.
 *  Backend-stored pyramid values pre-dating the icon pack don't carry `id`,
 *  so we infer it from the human name. e.g. "Open Mindedness" → "open_mindedness".
 */
const nameToId = (name) => {
  if (!name) return "";
  return name
    .toString()
    .toLowerCase()
    .trim()
    // "/" and "-" merge into underscore ("Studies / Learning" → "studies_learning")
    .replace(/[\/\-]+/g, "_")
    .replace(/\s+/g, "_")
    // Strip any stray punctuation (keeps alnum + underscore)
    .replace(/[^a-z0-9_]/g, "");
};

const ValueIcon = ({
  id,
  name,
  anti = false,
  size = 32,
  className = "",
  rounded = true,
  testId,
}) => {
  const [failed, setFailed] = React.useState(false);

  // Defensive: accept either `honesty` or `honesty.svg`, tolerate spaces.
  // Fall back to a slugified `name` when the record didn't persist `id`
  // (legacy rows written before the icon pack shipped).
  const rawId = (id || "").toString().trim().replace(/\.svg$/i, "").replace(/\s+/g, "_");
  const slug = rawId || nameToId(name);

  if (!slug || failed) {
    // Fallback: first-letter monogram so custom/future values never break.
    const letter = (name || slug || "?").charAt(0).toUpperCase();
    return (
      <span
        className={`inline-flex items-center justify-center text-white font-semibold shrink-0 ${
          anti ? FALLBACK_LETTERS_BG_ANTI : FALLBACK_LETTERS_BG
        } ${rounded ? "rounded-full" : "rounded-md"} ${className}`}
        style={{ width: size, height: size, fontSize: Math.max(10, size * 0.42) }}
        aria-label={name || slug}
        data-testid={testId}
      >
        {letter}
      </span>
    );
  }

  return (
    <img
      src={`/icons/values/${slug}.svg`}
      width={size}
      height={size}
      alt={name || slug}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`shrink-0 ${rounded ? "rounded-full" : ""} ${className}`}
      style={{ width: size, height: size }}
      data-testid={testId}
    />
  );
};

export default ValueIcon;
